import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import { getAuthHeaders } from "../../auth/services/getAuthHeaders";
import { parseMarkdown, downloadMarkdown, downloadHtml } from "../utils/utils";
import {
  X,
  Save,
  Mail,
  Copy,
  FileCode,
  Check,
  Send,
  Clock,
} from "lucide-react";
import { LuDownload } from "react-icons/lu";
import { FiEdit } from "react-icons/fi";
import { LoadingButton } from "../../../components/LoadingButton";
import { ErrorAlert } from "../../../components/ErrorAlert";
import { useSummaryGenerator } from "../hooks/useSummaryGenerator";
import { useSummaryEmail } from "../hooks/useSummaryEmail";

interface SummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  meetingTranscript: { timestamp: string; orig: string; tran: string; isMic?: boolean }[];
  currentMeetingId: string | null;
  summaryServiceUrl: string;
  translatorServiceUrl: string;
  targetLang: string;
  targetLangLabel: string;
  initialSummaryText?: string;
  initialTitle?: string;
  initialDuration?: string;
  initialTranscriptCount?: number;
  isSavedSummary?: boolean;
  appMode?: "traductor" | "minuta";
  onUpdateSavedTitle?: (newTitle: string) => void;
}

export function SummaryModal({
  isOpen,
  onClose,
  meetingTranscript,
  currentMeetingId,
  summaryServiceUrl,
  translatorServiceUrl,
  targetLang,
  targetLangLabel,
  initialSummaryText = "",
  initialTitle = "",
  initialDuration = "",
  initialTranscriptCount,
  isSavedSummary = false,
  appMode = "traductor",
  onUpdateSavedTitle,
}: SummaryModalProps) {
  const durMinStr =
    initialDuration ||
    (meetingTranscript.length > 0
      ? meetingTranscript[meetingTranscript.length - 1]?.timestamp || "0:03"
      : "0:00");

  const {
    summaryText,
    htmlFilename,
    loading,
    error,
    isPolling,
    backendDuration,
    transcriptCount,
    generateSummary,
    resetGeneration,
    setSavedSummary,
    setError,
  } = useSummaryGenerator({
    summaryServiceUrl,
    currentMeetingId,
    targetLang,
    targetLangLabel,
    meetingTranscript,
    duration: durMinStr,
  });

  const {
    emailInput,
    setEmailInput,
    emailLoading,
    emailStatus,
    emailMessage,
    emptyFieldError,
    setEmptyFieldError,
    emailInputRef,
    handleSendEmail,
    resetEmailState,
  } = useSummaryEmail(summaryServiceUrl, currentMeetingId);

  const [copied, setCopied] = useState(false);
  const [meetingTitle, setMeetingTitle] = useState("");
  const [lastSavedTitle, setLastSavedTitle] = useState("");
  const [renamingTitle, setRenamingTitle] = useState(false);
  const summaryContentRef = useRef<HTMLDivElement>(null);
  const lastSavedSummaryRef = useRef("");

  const now = new Date();
  const dateStr = now.toLocaleDateString("es-AR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const shortDateStr = now.toLocaleDateString("es-AR", {
    day: "numeric",
    month: "short",
  });
  const dateMetaStr = now.toLocaleDateString("es-AR");

  const segmentsCount = transcriptCount !== null ? transcriptCount : (initialTranscriptCount !== undefined ? initialTranscriptCount : meetingTranscript.length);
  const finalDurationStr = backendDuration || durMinStr;

  useEffect(() => {
    if (isOpen && currentMeetingId && durMinStr && durMinStr !== "0:00" && !isSavedSummary) {
      (async () => {
        try {
          const authHeaders = await getAuthHeaders();
          const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
          await fetch(`${cleanedUrl}/meetings/${currentMeetingId}`, {
            method: "PUT",
            headers: { ...authHeaders, "Content-Type": "application/json" },
            body: JSON.stringify({ duration: durMinStr }),
          });
        } catch (err) {
          console.warn("Error saving meeting duration:", err);
        }
      })();
    }
  }, [isOpen, currentMeetingId, durMinStr, isSavedSummary, translatorServiceUrl]);

  useEffect(() => {
    if (isOpen) {
      if (isSavedSummary) {
        setSavedSummary(initialSummaryText);
        const titleStr = initialTitle || "Minuta Guardada — Kira";
        setMeetingTitle(titleStr);
        setLastSavedTitle(titleStr);
      } else {
        const titleStr = initialTitle || `Reunión — ${shortDateStr}`;
        setMeetingTitle(titleStr);
        setLastSavedTitle(titleStr);
        if (meetingTranscript.length > 0 && !summaryText && !loading) {
          generateSummary();
        }
      }
    }
  }, [isOpen, currentMeetingId, isSavedSummary]);

  const handleRenameMeeting = async () => {
    if (!currentMeetingId || !meetingTitle.trim() || meetingTitle.trim() === lastSavedTitle || renamingTitle) {
      return;
    }
    const newTitle = meetingTitle.trim();
    setRenamingTitle(true);
    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const res = await fetch(`${cleanedUrl}/meetings/${currentMeetingId}`, {
        method: "PUT",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ title: newTitle }),
      });
      if (res.ok) {
        setLastSavedTitle(newTitle);
        if (onUpdateSavedTitle) {
          onUpdateSavedTitle(newTitle);
        }
        window.dispatchEvent(new CustomEvent("kira:meetings-changed"));
        toast.success(targetLang === "en" ? "Title updated" : "Título actualizado");
      } else {
        toast.error(targetLang === "en" ? "Could not update title" : "No se pudo actualizar el título");
      }
    } catch (err) {
      console.error("Error renaming meeting:", err);
      toast.error(targetLang === "en" ? "Could not update title" : "No se pudo actualizar el título");
    } finally {
      setRenamingTitle(false);
    }
  };

  useEffect(() => {
    if (summaryText && !lastSavedSummaryRef.current) {
      lastSavedSummaryRef.current = summaryText;
    }
  }, [summaryText]);

  const saveSummaryToBackend = async (textOverride?: string) => {
    let currentText = textOverride ?? (summaryContentRef.current?.innerText || summaryText);
    if (!currentText) return currentText;

    if (!currentText.trim().startsWith("# ") && summaryText) {
      const firstLine = (summaryText.split("\n")[0] || "").trim();
      if (firstLine.startsWith("# ")) {
        currentText = `${firstLine}\n\n${currentText.trim()}`;
      }
    }

    if (currentText.trim() === lastSavedSummaryRef.current.trim()) {
      return currentText;
    }

    lastSavedSummaryRef.current = currentText;
    setSavedSummary(currentText);
    return currentText;
  };

  useEffect(() => {
    if (!isOpen) {
      resetGeneration();
      resetEmailState();
    }
  }, [isOpen, resetGeneration, resetEmailState]);

  const handleCloseModal = async () => {
    await saveSummaryToBackend();
    onClose();
  };

  const handleSendWithSave = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveSummaryToBackend();
    await handleSendEmail(e);
  };

  const handleCopy = async () => {
    const latestSummary = await saveSummaryToBackend();
    if (!latestSummary) return;
    try {
      await navigator.clipboard.writeText(latestSummary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = latestSummary;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        toast.error("No se pudo copiar automáticamente.");
      }
    }
  };

  if (!isOpen) return null;

  const isTranslate = appMode === "traductor";

  let cardTitleStr = "Minuta de reunión";
  let bodySummaryText = summaryText;
  if (summaryText) {
    let lines = summaryText.split("\n");
    while (lines.length > 0) {
      const lineTrimmed = (lines[0] || "").trim();
      if (!lineTrimmed || /^([#*\-\s]*)(duraci[oó]n|duration|duraç[aã]o|dauer|durée)\s*[*:#=]/i.test(lineTrimmed)) {
        lines.shift();
      } else {
        break;
      }
    }
    const firstLineTrimmed = (lines[0] || "").trim();
    if (/^#+\s+/.test(firstLineTrimmed)) {
      cardTitleStr = firstLineTrimmed.replace(/^#+\s*/, "").replace(/\*+/g, "").trim();
      lines.shift();
      while (lines.length > 0 && !(lines[0] || "").trim()) {
        lines.shift();
      }
    }
    lines = lines.filter((line) => !/^([#*\-\s]*)(duraci[oó]n|duration|duraç[aã]o|dauer|durée)\s*[*:#=]\s*[0-9]+/i.test(line.trim()));
    bodySummaryText = lines.join("\n");
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4"
      onClick={handleCloseModal}
    >
      <div
        className="bg-[var(--bg2)] border border-[var(--b2)] rounded-[14px] w-full max-w-[700px] max-h-[88vh] shadow-[0_24px_60px_rgba(0,0,0,0.65)] flex flex-col overflow-hidden animate-[modal-appear_0.22s_cubic-bezier(0.16,1,0.3,1)] text-[var(--text)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="summary-modal-title"
      >
        {/* Header exact to prototype */}
        <div className="px-4 py-3.5 border-b border-[var(--b1)] flex items-center gap-2.5 flex-shrink-0 bg-transparent">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--accent)"
            strokeWidth="2"
            strokeLinecap="round"
            className="flex-shrink-0"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
          <span
            id="summary-modal-title"
            className="font-semibold text-sm text-[var(--t1)]"
          >
            Minuta de reunión
          </span>
          <span className="text-[11px] text-[var(--t3)] ml-1">
            {dateStr}
          </span>
          <span className="text-[11px] text-[var(--t3)] ml-2 flex items-center gap-1">
            <Clock size={11} className="text-[var(--t3)]" />
            <span>{finalDurationStr} min</span>
          </span>
          <button
            onClick={handleCloseModal}
            className="ml-auto w-[22px] h-[22px] rounded-full bg-[var(--pill)] border border-[var(--b1)] flex items-center justify-center text-[var(--t2)] hover:bg-[var(--bg4)] hover:text-[var(--t1)] transition-colors cursor-pointer"
            aria-label="Cerrar modal"
          >
            <X size={12} />
          </button>
        </div>

        {/* Editable meeting title bar */}
        <div className="flex items-center gap-1.5 px-4 pt-3 pb-1 flex-shrink-0">
          <input
            type="text"
            value={meetingTitle}
            onChange={(e) => setMeetingTitle(e.target.value)}
            onBlur={handleRenameMeeting}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
            disabled={renamingTitle}
            className="font-sans text-[15px] font-bold text-[var(--t1)] bg-transparent border-b-[1.5px] border-transparent hover:bg-[var(--pill)] focus:bg-[var(--pill)] focus:border-b-[var(--accent)] px-1.5 py-1 rounded-[4px] outline-none transition-all flex-1"
            placeholder="Título de la reunión..."
          />
          {renamingTitle ? (
            <Clock size={13} className="animate-spin text-[var(--accent)] flex-shrink-0 mr-2" />
          ) : (
            <FiEdit
              size={13}
              className="text-[var(--t3)] flex-shrink-0 mr-2"
            />
          )}
        </div>

        {/* Mode / context callout banner */}
        <div className="px-4 py-2.5 border-b border-[var(--b1)] flex items-start gap-2.5 flex-shrink-0 mt-1">
          <div
            className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
              isTranslate
                ? "bg-blue-500/12 text-accent"
                : "bg-emerald-500/12 text-emerald-500"
            }`}
          >
            {isTranslate ? (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M5 3l14 9-14 9V3z" />
                <path d="M19 3v18" />
              </svg>
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            )}
          </div>
          <div className="text-xs text-[var(--t2)] leading-relaxed">
            <strong className="text-[var(--t1)] block mb-0.5 font-semibold text-[12.5px]">
              {isSavedSummary
                ? "Minuta guardada"
                : isTranslate
                ? "Modo Traductor + Minuta"
                : "Modo Solo Minuta"}
            </strong>
            <span>
              {isSavedSummary
                ? "Podés consultar o exportar el contenido, descargarlo en formato .md o .html, o reenviarlo por correo."
                : isTranslate
                ? "Se capturó el audio en modo bilingüe. El resumen incluye el contexto completo de la conversación con sus traducciones. Podés editar antes de exportar."
                : "Sesión registrada en modo de registro puro. La minuta fue generada automáticamente al detener. Revisá y editá antes de enviar."}
            </span>
          </div>
        </div>

        {/* Body content */}
        <div className="p-4 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
              <div className="w-8 h-8 border-[2.5px] border-accent/20 border-t-accent rounded-full animate-spin"></div>
              <p className="font-sans text-xs text-[var(--t2)]">
                {isPolling
                  ? "La IA está procesando la minuta..."
                  : "Generando minuta…"}
              </p>
              {isPolling && (
                <p className="text-[10px] text-[var(--t3)] max-w-xs leading-relaxed">
                  La generación tomó más de 29s. La Lambda sigue corriendo en
                  segundo plano — te avisamos cuando termine.
                </p>
              )}
            </div>
          )}

          {error && (
            <ErrorAlert error={error} onDismiss={() => setError("")}>
              <button
                onClick={generateSummary}
                className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded text-xs text-red-300 font-medium transition-all"
              >
                Reintentar
              </button>
            </ErrorAlert>
          )}

          {!loading && !error && summaryText && (
            <div className="flex flex-col gap-3">
              {/* Inner card exactly matching prototype */}
              <div
                className="bg-[var(--bg3)] border border-[var(--b1)] rounded-[10px] p-[18px_20px] text-[13px] leading-[1.75] text-[var(--t2)] outline-none select-text"
              >
                <h2 className="text-[15px] font-bold text-[var(--t1)] mb-1 flex items-center gap-2">
                  <span>{cardTitleStr}</span>
                </h2>
                <div className="text-[10.5px] text-[var(--t3)] mb-3.5 pb-3 border-b border-[var(--b1)]">
                  Duración: {finalDurationStr} min · {segmentsCount}{" "}
                  segmentos · {dateMetaStr}
                </div>

                <div
                  ref={summaryContentRef}
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={() => saveSummaryToBackend()}
                  className="outline-none focus:bg-white/[0.02] p-1 -m-1 rounded transition-colors"
                  dangerouslySetInnerHTML={{
                    __html: parseMarkdown(bodySummaryText),
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer actions exactly matching prototype */}
        {!loading && (summaryText || error) && (
          <div className="px-4 pt-3 pb-4 bg-transparent border-t border-[var(--b1)] flex items-start justify-between gap-2 flex-wrap flex-shrink-0 min-h-[66px]">
            {summaryText && (
              <div className="flex items-center gap-1.5 flex-1 min-w-[200px]">
                {emailMessage ? (
                  <div
                    className={`w-full px-3 py-1.5 rounded-md text-xs font-medium flex items-center transition-all ${
                      emailStatus === "success"
                        ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400"
                        : "bg-red-500/10 border border-red-500/20 text-red-400"
                    }`}
                  >
                    {emailMessage}
                  </div>
                ) : (
                  <div className="flex flex-col gap-1 w-full relative">
                    <form
                      onSubmit={handleSendWithSave}
                      className="flex items-center gap-1.5 w-full"
                    >
                      <Mail
                        size={14}
                        className="text-[var(--t3)] flex-shrink-0"
                      />
                      <input
                        ref={emailInputRef}
                        type="email"
                        placeholder="Enviar a correo…"
                        value={emailInput}
                        onChange={(e) => {
                          setEmailInput(e.target.value);
                          if (emptyFieldError) setEmptyFieldError(false);
                        }}
                        disabled={emailLoading}
                        className={`w-full text-xs font-sans bg-[var(--bg3)] border ${
                          emptyFieldError ? "border-red-500/80" : "border-[var(--b1)]"
                        } text-[var(--t1)] rounded-md px-2.5 py-1.5 outline-none focus:border-accent placeholder-[var(--t3)] transition-colors flex-1 min-w-0`}
                      />
                      <LoadingButton
                        type="submit"
                        isLoading={emailLoading}
                        className="px-3 py-1.5 bg-accent hover:filter hover:brightness-110 disabled:opacity-50 text-white font-semibold text-xs rounded-md transition-all flex items-center gap-1 whitespace-nowrap shadow-sm shadow-accent/20 cursor-pointer"
                      >
                        {emailLoading ? (
                          "Enviando..."
                        ) : (
                          <>
                            <Send size={11} />
                            <span>Enviar</span>
                          </>
                        )}
                      </LoadingButton>
                    </form>
                    {emptyFieldError && (
                      <span className="text-[10px] text-red-400 font-medium absolute -bottom-3.5 left-6 whitespace-nowrap">
                        Por favor, ingresa un correo electrónico
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
              {summaryText && (
                <>
                  <button
                    onClick={handleCopy}
                    className="px-3 py-1.5 bg-[var(--pill)] border border-[var(--b1)] hover:bg-[var(--bg4)] hover:border-[var(--b2)] text-[var(--t2)] hover:text-[var(--t1)] rounded-md text-xs font-medium transition-all flex items-center gap-1 active:scale-95 cursor-pointer"
                  >
                    {copied ? (
                      <>
                        <Check size={11} className="text-emerald-500" />
                        <span className="text-emerald-500">Copiado</span>
                      </>
                    ) : (
                      <>
                        <Copy size={11} />
                        <span>Copiar</span>
                      </>
                    )}
                  </button>

                  <button
                    onClick={async () => {
                      const latest = await saveSummaryToBackend();
                      if (latest) downloadMarkdown(latest);
                      toast.success("Descargando .md…");
                    }}
                    className="px-3 py-1.5 bg-[var(--pill)] border border-[var(--b1)] hover:bg-[var(--bg4)] hover:border-[var(--b2)] text-[var(--t2)] hover:text-[var(--t1)] rounded-md text-xs font-medium transition-all flex items-center gap-1 active:scale-95 cursor-pointer"
                  >
                    <LuDownload size={12} />
                    <span>.md</span>
                  </button>

                  <button
                    onClick={async () => {
                      const latest = await saveSummaryToBackend();
                      if (latest) downloadHtml(latest);
                      toast.success("Descargando .html…");
                    }}
                    className="px-3 py-1.5 bg-emerald-500/15 border border-emerald-500/25 hover:bg-emerald-500/22 text-emerald-400 rounded-md text-xs font-medium transition-all flex items-center gap-1 active:scale-95 cursor-pointer"
                  >
                    <LuDownload size={12} />
                    <span>.html</span>
                  </button>
                </>
              )}

              <button
                onClick={async () => {
                  await saveSummaryToBackend();
                  toast.success("Minuta guardada");
                  onClose();
                }}
                className="px-3.5 py-1.5 bg-accent hover:filter hover:brightness-110 text-white rounded-md text-xs font-semibold transition-all active:scale-95 shadow-sm shadow-accent/20 cursor-pointer ml-1"
              >
                Listo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
